import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
} from '@mui/material';
import {
  ExpandMore,
  Science,
  Lightbulb,
  Info,
  CheckCircle,
} from '@mui/icons-material';
import SupportShell from './SupportShell';

const AgronomicPrinciplesPage: React.FC = () => {
  const navigate = useNavigate();
  const [expandedSection, setExpandedSection] = useState<string | false>('nutrient-uptake');

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  const sections = [
    {
      id: 'nutrient-uptake',
      title: 'Nutrient Uptake Mechanisms',
      icon: <Science color="primary" />,
      content: {
        overview: 'Understanding how plants absorb and utilize nutrients in hydroponic systems.',
        keyPoints: [
          'Active vs. passive nutrient transport',
          'Root zone pH effects on nutrient availability',
          'Nutrient mobility within plant tissues',
          'Antagonistic and synergistic nutrient interactions',
        ],
        details: `Plants absorb nutrients through their root systems via two primary mechanisms: active transport and passive transport. Active transport requires energy (ATP) and allows plants to absorb nutrients against concentration gradients, while passive transport relies on diffusion and mass flow.

The pH of the root zone significantly affects nutrient availability. Most nutrients are optimally available at pH 5.5-6.5 in hydroponic systems. Outside this range, certain nutrients may become unavailable even if present in the solution.`,
        tips: [
          'Monitor root zone pH regularly for optimal nutrient uptake',
          'Understand that some nutrients compete for uptake sites',
          'Consider nutrient mobility when diagnosing deficiencies',
        ],
      },
    },
    {
      id: 'ph-management',
      title: 'pH Management & Buffer Systems',
      icon: <Science color="primary" />,
      content: {
        overview: 'Mastering pH control and understanding buffer capacity in hydroponic solutions.',
        keyPoints: [
          'Henderson-Hasselbalch equation applications',
          'Buffer capacity and alkalinity relationships',
          'pH adjustment strategies and timing',
          'Water quality impact on pH stability',
        ],
        details: `pH management is critical for nutrient availability and plant health. The Henderson-Hasselbalch equation (pH = pKa + log([A-]/[HA])) governs buffer systems in hydroponic solutions.

Buffer capacity determines how resistant a solution is to pH changes. Higher alkalinity water requires more acid to achieve target pH and provides more pH stability over time.`,
        tips: [
          'Test water alkalinity before formulating solutions',
          'Use appropriate acids/bases for pH adjustment',
          'Monitor pH drift patterns to optimize adjustment timing',
        ],
      },
    },
    {
      id: 'ec-optimization',
      title: 'EC Optimization & Ionic Strength',
      icon: <Science color="primary" />,
      content: {
        overview: 'Understanding electrical conductivity and its relationship to plant nutrition.',
        keyPoints: [
          'EC vs. TDS measurements and conversions',
          'Ionic strength effects on nutrient availability',
          'Osmotic pressure and plant water relations',
          'Stage-specific EC requirements',
        ],
        details: `Electrical Conductivity (EC) measures the total dissolved salts in solution and correlates with osmotic pressure. Higher EC solutions require more energy for plants to absorb water, affecting growth rates and nutrient uptake efficiency.

The LMCv2 model used in FlahaFast accounts for ionic strength effects on conductivity, providing more accurate EC predictions than simple additive models.`,
        tips: [
          'Adjust EC based on growth stage and environmental conditions',
          'Consider individual ion contributions to total EC',
          'Monitor plant response to EC changes over time',
        ],
      },
    },
    {
      id: 'plant-physiology',
      title: 'Plant Physiology & Growth Stages',
      icon: <Science color="primary" />,
      content: {
        overview: 'Connecting plant physiological needs to nutrient management strategies.',
        keyPoints: [
          'Growth stage nutrient requirements',
          'Photosynthesis and nutrient demand relationships',
          'Root development and nutrient absorption',
          'Environmental factors affecting nutrient needs',
        ],
        details: `Plant nutrient requirements change dramatically throughout growth stages. Seedlings require lower EC and different N:P:K ratios compared to flowering plants. Understanding these physiological changes allows for optimized nutrition programs.

Environmental factors like light intensity, temperature, and humidity affect nutrient demand. Higher light levels increase photosynthetic activity and nutrient requirements, while temperature affects root metabolism and uptake rates.`,
        tips: [
          'Adjust formulations based on growth stage',
          'Consider environmental conditions when setting nutrient levels',
          'Monitor plant tissue analysis for optimization feedback',
        ],
      },
    },
  ];

    return (
    <SupportShell
      title="Agronomic principles"
      subtitle="Nutrient uptake, pH, EC, and plant physiology for hydroponic systems."
    >

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Why this matters
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Recipe quality depends on plant physiology, nutrient chemistry, and root-zone conditions.
                Use this guide as foundation knowledge when setting targets and interpreting Results.

              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                Master these concepts to optimize your nutrient formulations, troubleshoot plant health issues, 
                and achieve consistent, high-quality yields in your hydroponic systems.
              </Typography>
            </CardContent>
          </Card>

          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Learning objectives
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText primary="Understand nutrient uptake mechanisms and plant physiology" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText primary="Master pH management and buffer system principles" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText primary="Optimize EC levels based on growth stages and conditions" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText primary="Apply physiological knowledge to nutrient management strategies" />
              </ListItem>
            </List>
          </Paper>

          {/* Content Sections */}
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 4 }}>
            Core Topics
          </Typography>

          {sections.map((section) => (
            <Accordion
              key={section.id}
              expanded={expandedSection === section.id}
              onChange={handleAccordionChange(section.id)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{ 
                  '& .MuiAccordionSummary-content': { 
                    alignItems: 'center',
                    gap: 2 
                  }
                }}
              >
                {section.icon}
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                  {section.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 4 }}>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                  {section.content.overview}
                </Typography>

                <Typography variant="h6" component="h4" sx={{ fontWeight: 600, mb: 2 }}>
                  Key Concepts
                </Typography>
                <List sx={{ mb: 3 }}>
                  {section.content.keyPoints.map((point, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={point} />
                    </ListItem>
                  ))}
                </List>

                <Typography variant="h6" component="h4" sx={{ fontWeight: 600, mb: 2 }}>
                  Detailed Explanation
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                  {section.content.details}
                </Typography>

                <Paper sx={{ p: 3, bgcolor: 'warning.50' }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Lightbulb color="warning" />
                    <Box>
                      <Typography variant="h6" component="h5" sx={{ fontWeight: 600, mb: 2 }}>
                        Professional Tips
                      </Typography>
                      <List>
                        {section.content.tips.map((tip, index) => (
                          <ListItem key={index} sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <Info color="info" sx={{ fontSize: 20 }} />
                            </ListItemIcon>
                            <ListItemText primary={tip} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Stack>
                </Paper>
              </AccordionDetails>
            </Accordion>
          ))}

          <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
            <Button size="small" variant="contained" onClick={() => navigate('/support')}>
              All guides
            </Button>
            <Button size="small" variant="outlined" onClick={() => navigate('/login')}>
              Open app
            </Button>
          </Stack>
    </SupportShell>
  );
};

export default AgronomicPrinciplesPage;
